import type { Router } from "vue-router";
import { getCurrentInstance, inject } from "vue";

/**
 * 获取当前应用的router实例
 * 优先使用inject方式，然后尝试从Vue应用的全局属性中获取router实例
 */
export function getRouterInstance(): Router | undefined {
  // 方案1: 优先使用inject方式获取router (Vue 3推荐方式)
  try {
    const router = inject<Router>("$router");
    if (router) {
      console.log("通过inject获取到router实例");
      return router;
    }
  } catch (error) {
    console.log("inject方式获取router失败:", error);
  }

  // 方案2: 尝试通过getCurrentInstance获取
  const instance = getCurrentInstance();
  console.log("获取到的instance实例:", instance);

  if (instance) {
    // 2.1 从全局属性获取router
    if (instance.appContext.app.config.globalProperties.$router) {
      console.log("通过globalProperties获取到router实例");
      return instance.appContext.app.config.globalProperties.$router;
    }

    // 2.2 从provides中获取router
    try {
      const router = instance.appContext.provides?.router || instance.appContext.provides?.["$router"];
      if (router) {
        console.log("通过provides获取到router实例");
        return router;
      }
    } catch (error) {
      console.warn("从provides获取router失败:", error);
    }

    // 2.3 尝试从app实例直接获取
    try {
      const app = instance.appContext.app;
      if (app && app._context && app._context.provides) {
        const router = app._context.provides.router || app._context.provides["$router"];
        if (router) {
          console.log("通过app._context.provides获取到router实例");
          return router;
        }
      }
    } catch (error) {
      console.warn("从app._context.provides获取router失败:", error);
    }
  }

  console.warn("无法获取router实例，所有方法都失败了");
  return undefined;
}

/**
 * 安全地获取当前路由信息
 */
export function getCurrentRoute() {
  const router = getRouterInstance();
  return router?.currentRoute.value;
}

/**
 * 安全地进行路由跳转
 */
export function routerPush(to: string | object) {
  const router = getRouterInstance();
  if (router) {
    return router.push(to);
  } else {
    console.warn("Router实例不可用，无法进行路由跳转");
    return Promise.reject(new Error("Router实例不可用"));
  }
}

/**
 * 安全地进行路由替换
 */
export function routerReplace(to: string | object) {
  const router = getRouterInstance();
  if (router) {
    return router.replace(to);
  } else {
    console.warn("Router实例不可用，无法进行路由替换");
    return Promise.reject(new Error("Router实例不可用"));
  }
}

/**
 * 安全地返回上一页
 */
export function routerBack() {
  const router = getRouterInstance();
  if (router) {
    router.back();
  } else {
    console.warn("Router实例不可用，无法返回上一页");
    // 兜底方案：使用浏览器原生返回
    if (typeof window !== "undefined" && window.history) {
      window.history.back();
    }
  }
}
